import CommonDialog from "../../../../common/CommonDialog/CommonDialog";
import {
  ApiErrorHandler,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { Button, Divider, TextField } from "@mui/material";
import {
  IGroupInfo,
  ISuggestedClinicianData,
  IUserInfo,
} from "@home-health-notify/domain";
import { useContext, useEffect, useState } from "react";
import { SendReferralDetailsWrapper } from "./SmartContent.style";
import { ClinicianDetails } from "./ClinicianDetails";
import { useSnackbar } from "notistack";
import { ErrorMessage } from "../../../../utils/ErrorMessage";

export const SendReferral = ({
  isSendReferralModalOpen,
  closeSendReferralModal,
  clinicianData,
  groupInfo,
  userInfo,
}: {
  isSendReferralModalOpen: boolean;
  closeSendReferralModal: () => void;
  clinicianData: ISuggestedClinicianData;
  groupInfo: IGroupInfo;
  userInfo: IUserInfo;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });

  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();
  const [message, setMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isSendReferralModalOpen) {
      resetState();
    }
  }, [isSendReferralModalOpen]);

  const resetState = () => {
    setMessage("");
  };

  const onSetReferralMessage = (message: string) => {
    setMessage(message);
  };

  const onSendReferral = async (selectedId: string) => {
    setIsLoading(true);
    const chatId = groupInfo.id;

    let referralToSend: any = {
      referralsToSend: [
        {
          userId: selectedId,
          referralMessage: message,
        },
      ],
    };

    try {
      if (selectedOrgUser) {
        await selectedOrgUser.chatManager
          .getPatientChatManager()
          .addMembers(referralToSend, chatId);
        enqueueSnackbar(
          tNs("referral.referralSentSuccessfullyTo") + userInfo.name,
          {
            autoHideDuration: 3000,
          },
        );
      }
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar, ErrorMessage.SEND_REFERRAL_ERROR);
    } finally {
      setIsLoading(false);
      closeSendReferralModal();
    }
  };

  return (
    <CommonDialog
      isDialogOpen={isSendReferralModalOpen}
      onDialogClose={closeSendReferralModal}
      title={tNs("smartContent.sendReferralModalTitle")}
      actionContent={
        <Button
          variant="contained"
          data-testid="send-referral-btn-modal"
          onClick={() => onSendReferral(clinicianData.id)}
          disabled={isLoading}
          loadingPosition="center"
          loading={isLoading}
        >
          {tNs("referral.sendReferral")}
        </Button>
      }
    >
      <SendReferralDetailsWrapper data-testid="send-referral-modal">
        <ClinicianDetails
          clinicianData={clinicianData}
          inReferralModal={true}
        />
        <Divider sx={{ marginTop: "2px" }} />
        <TextField
          id="referral-message-textField"
          label={tNs("smartContent.enterReferralMessage")}
          multiline
          fullWidth
          variant="standard"
          rows={4}
          sx={{ mb: 5 }}
          data-testid="referral-message-text-field"
          onChange={e => {
            onSetReferralMessage(e.target.value);
          }}
        />
      </SendReferralDetailsWrapper>
    </CommonDialog>
  );
};
